import Game from "./Game";
import { useEffect, useState } from "react";
import { getAgritechLanguage } from "./utils/agritechText";
import { auth, db, googleProvider } from "./firebase";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { signInWithPopup } from "firebase/auth";
import * as XLSX from "xlsx";

const MOBILE_CONTROL_NAMES = [
  "up",
  "left",
  "down",
  "right",
  "z",
  "x",
  "one",
  "two",
  "three",
  "p",
];

const dispatchMobileControl = (control, pressed) => {
  window.dispatchEvent(
    new CustomEvent("biorev-mobile-control", {
      detail: { control, pressed },
    }),
  );
};

function MobileControlButton({
  control,
  label,
  className = "",
  hidden = false,
  isPower = false,
  powerHeld = false,
  holdGlow = false,
}) {
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    if (hidden) {
      setPressed(false);
    }
  }, [hidden]);

  const releaseControl = (event) => {
    event.preventDefault();
    setPressed(false);
    dispatchMobileControl(control, false);
  };

  return (
    <button
      type="button"
      aria-label={label}
      aria-hidden={hidden}
      disabled={hidden}
      className={`mobile-control-button ${className} ${
        hidden ? "mobile-control-hidden" : ""
      } ${isPower ? "mobile-power-button" : ""} ${
        isPower && powerHeld ? "mobile-power-held" : ""
      } ${holdGlow && pressed ? "mobile-control-pressed" : ""}`}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        if (holdGlow) {
          setPressed(true);
        }

        dispatchMobileControl(control, true);
      }}
      onPointerUp={releaseControl}
      onPointerCancel={releaseControl}
      onLostPointerCapture={releaseControl}
    >
      {label}
    </button>
  );
}

function MobileGameControls({ isSpanish }) {
  const [mobileUi, setMobileUi] = useState({
    mode: "hidden",
    visibleControls: [],
    powerStates: {
      one: false,
      two: false,
      three: false,
    },
  });

  useEffect(() => {
    const releaseAllControls = () => {
      MOBILE_CONTROL_NAMES.forEach((control) => {
        dispatchMobileControl(control, false);
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) releaseAllControls();
    };

    const handleMobileUi = (event) => {
      const detail = event.detail ?? {};
      const mode = detail.mode ?? "hidden";

      setMobileUi({
        mode,
        visibleControls: Array.isArray(detail.visibleControls)
          ? detail.visibleControls
          : [],
        powerStates: {
          one: detail.powerStates?.one === true,
          two: detail.powerStates?.two === true,
          three: detail.powerStates?.three === true,
        },
      });

      document.documentElement.dataset.biorevMobileScene = mode;
      document.documentElement.dataset.biorevMobilePanel =
        detail.hideRightPanel === true ? "hidden" : "visible";

      if (mode === "hidden") {
        releaseAllControls();
      }
    };

    document.documentElement.dataset.biorevMobileScene = "hidden";
    document.documentElement.dataset.biorevMobilePanel = "visible";

    window.addEventListener("blur", releaseAllControls);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("biorev-mobile-ui", handleMobileUi);

    return () => {
      releaseAllControls();
      window.removeEventListener("blur", releaseAllControls);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("biorev-mobile-ui", handleMobileUi);

      delete document.documentElement.dataset.biorevMobileScene;
      delete document.documentElement.dataset.biorevMobilePanel;
    };
  }, []);

  const controlIsVisible = (control) =>
    mobileUi.visibleControls.includes(control);

  return (
    <>
      <div className="mobile-rotate-overlay">
        <div className="mobile-rotate-icon">{"\u21BB"}</div>
        <p>
          {isSpanish
            ? "Gira tu dispositivo para jugar"
            : "Rotate your device to play"}
        </p>
      </div>

      <div
        className={`mobile-control-layer ${
          mobileUi.mode === "hidden" ? "mobile-control-layer-hidden" : ""
        }`}
      >
        <div className="mobile-left-controls">
          <div className="mobile-dpad">
            <MobileControlButton
              control="up"
              label={"\u2191"}
              className="mobile-up"
              holdGlow
              hidden={!controlIsVisible("up")}
            />

            <MobileControlButton
              control="left"
              label={"\u2190"}
              className="mobile-left"
              holdGlow
              hidden={!controlIsVisible("left")}
            />

            <MobileControlButton
              control="down"
              label={"\u2193"}
              className="mobile-down"
              holdGlow
              hidden={!controlIsVisible("down")}
            />

            <MobileControlButton
              control="right"
              label={"\u2192"}
              className="mobile-right"
              holdGlow
              hidden={!controlIsVisible("right")}
            />
          </div>
        </div>

        <div className="mobile-action-pad">
          <div className="mobile-power-pad">
            <MobileControlButton
              control="one"
              label="1"
              isPower
              hidden={!controlIsVisible("one")}
              powerHeld={mobileUi.powerStates.one}
            />

            <MobileControlButton
              control="two"
              label="2"
              isPower
              hidden={!controlIsVisible("two")}
              powerHeld={mobileUi.powerStates.two}
            />

            <MobileControlButton
              control="three"
              label="3"
              isPower
              hidden={!controlIsVisible("three")}
              powerHeld={mobileUi.powerStates.three}
            />
          </div>

          <MobileControlButton
            control="p"
            label={isSpanish ? "PAUSA" : "PAUSE"}
            className="mobile-pause-button"
            hidden={!controlIsVisible("p")}
          />

          <div className="mobile-zx-stack">
            <MobileControlButton
              control="x"
              label={isSpanish ? "CORRER" : "RUN"}
              holdGlow
              hidden={!controlIsVisible("x")}
            />

            <MobileControlButton
              control="z"
              label={isSpanish ? "SALTAR" : "JUMP"}
              holdGlow
              hidden={!controlIsVisible("z")}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function App() {
  const [panelResetKey, setPanelResetKey] = useState(0);
  const [language, setLanguage] = useState(getAgritechLanguage());
  const [playerModalOpen, setPlayerModalOpen] = useState(false);
  const [playerModalStep, setPlayerModalStep] = useState("age");
  const [playerDocId, setPlayerDocId] = useState(null);
  const [playerSubmitting, setPlayerSubmitting] = useState(false);
  const [minorIdStatus, setMinorIdStatus] = useState("idle");
  const [adultNameStatus, setAdultNameStatus] = useState("idle");
  const [adultNameMatches, setAdultNameMatches] = useState([]);
  const [gameLoadingVisible, setGameLoadingVisible] = useState(false);
  const [gameLoadingProgress, setGameLoadingProgress] = useState(0);

  const [playerForm, setPlayerForm] = useState({
    fullName: "",
    isAdult: null,
    cedula: "",
    email: "",
    participantId: "",
  });

  useEffect(() => {
    const resetRightPanel = () => setPanelResetKey((value) => value + 1);
    window.addEventListener("reset-right-panel", resetRightPanel);
    return () =>
      window.removeEventListener("reset-right-panel", resetRightPanel);
  }, []);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLanguage(getAgritechLanguage());
      setPanelResetKey((value) => value + 1);
    };

    window.addEventListener("agritech-language-change", handleLanguageChange);
    return () =>
      window.removeEventListener(
        "agritech-language-change",
        handleLanguageChange,
      );
  }, []);

  useEffect(() => {
    const startGameLoading = () => {
      setGameLoadingProgress(0);
      setGameLoadingVisible(true);
    };

    const updateGameLoading = (event) => {
      const progress = Number(event.detail?.progress ?? 0);
      setGameLoadingProgress(Math.max(0, Math.min(1, progress)));
    };

    const finishGameLoading = () => {
      setGameLoadingProgress(1);
      setGameLoadingVisible(false);
    };

    window.addEventListener("biorev-game-loading-start", startGameLoading);
    window.addEventListener(
      "biorev-game-loading-progress",
      updateGameLoading,
    );
    window.addEventListener("biorev-game-ready", finishGameLoading);

    return () => {
      window.removeEventListener(
        "biorev-game-loading-start",
        startGameLoading,
      );
      window.removeEventListener(
        "biorev-game-loading-progress",
        updateGameLoading,
      );
      window.removeEventListener("biorev-game-ready", finishGameLoading);
    };
  }, []);

  useEffect(() => {
    const openPlayerModal = () => {
      setPlayerModalStep("age");
      setPlayerDocId(null);
      setPlayerSubmitting(false);
      setMinorIdStatus("idle");
      setAdultNameStatus("idle");
      setAdultNameMatches([]);

      setPlayerForm({
        fullName: "",
        isAdult: null,
        cedula: "",
        email: "",
        participantId: "",
      });

      setPlayerModalOpen(true);
    };

    window.addEventListener("agritech-open-player-modal", openPlayerModal);

    return () =>
      window.removeEventListener("agritech-open-player-modal", openPlayerModal);
  }, []);

  useEffect(() => {
    const loadLeaderboard = async () => {
      const q = query(
        collection(db, "biorevGameSessions"),
        where("status", "==", "finished"),
        orderBy("finalScore", "desc"),
      );

      const snapshot = await getDocs(q);
      const bestByParticipant = new Map();

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const participantId = data.participantId ?? data.cedula ?? "";
        const isAdult = data.isAdult === true;
        const participantKey =
          data.participantKey ??
          `${isAdult ? "adult" : "minor"}:${participantId}`;
        const finalScore = data.finalScore ?? 0;

        if (!participantId) return;

        const currentBest = bestByParticipant.get(participantKey);

        if (!currentBest || finalScore > currentBest.finalScore) {
          bestByParticipant.set(participantKey, {
            isAdult,
            participantId: isAdult ? "" : participantId,
            initials: data.initials ?? "??",
            finalScore,
          });
        }
      });

      const rows = Array.from(bestByParticipant.values()).sort(
        (a, b) => b.finalScore - a.finalScore,
      );

      window.dispatchEvent(
        new CustomEvent("biorev-leaderboard-ready", {
          detail: { rows },
        }),
      );
    };

    window.addEventListener("biorev-request-leaderboard", loadLeaderboard);

    return () => {
      window.removeEventListener("biorev-request-leaderboard", loadLeaderboard);
    };
  }, []);

  useEffect(() => {
    const allowedDownloadEmails = [
      "lcbeltran92@gmail.com",
      "candrea.marinm@gmail.com",
      "camarin@ces.edu.co",
      "pazapata@ces.edu.co",
      "val.sae9@gmail.com",
    ];

    const downloadExcel = async () => {
      try {
        let user = auth.currentUser;

        if (!user) {
          const result = await signInWithPopup(auth, googleProvider);
          user = result.user;
        }

        const email = user?.email?.toLowerCase() ?? "";

        if (!allowedDownloadEmails.includes(email)) {
          alert(
            language === "es"
              ? "No tienes permiso para descargar estos datos."
              : "You do not have permission to download these data.",
          );
          return;
        }

        const playersSnapshot = await getDocs(collection(db, "biorevPlayers"));
        const sessionsSnapshot = await getDocs(
          collection(db, "biorevGameSessions"),
        );

        const formatTimestamp = (value) => {
          if (!value) return "";

          if (typeof value.toDate === "function") {
            return value.toDate().toLocaleString();
          }

          return value;
        };

        const getFirstAnswerByTier = (answers, tier) => {
          return answers.find(
            (answer) => answer.tier === tier && !answer.recoveryAttempt,
          );
        };

        const getRecoveryAnswerByTier = (answers, tier) => {
          return answers.find(
            (answer) => answer.tier === tier && answer.recoveryAttempt,
          );
        };

        const playersRows = playersSnapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          return {
            fullName: data.fullName ?? "",
            isAdult: data.isAdult ?? "",
            ageGroup: data.ageGroup ?? "",
            id: docSnap.id,
            cedula: data.cedula ?? "",
            participantId: data.participantId ?? "",
            participantKey: data.participantKey ?? "",
            email: data.email ?? "",
            createdAt: formatTimestamp(data.createdAt),
          };
        });

        const sessionRows = sessionsSnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const answers = data.answers ?? [];

          const tier1 = getFirstAnswerByTier(answers, "tier1");
          const tier2 = getFirstAnswerByTier(answers, "tier2");
          const tier3 = getFirstAnswerByTier(answers, "tier3");

          const tier1Recovery = getRecoveryAnswerByTier(answers, "tier1");
          const tier2Recovery = getRecoveryAnswerByTier(answers, "tier2");
          const tier3Recovery = getRecoveryAnswerByTier(answers, "tier3");

          const recoveryScore = answers
            .filter((answer) => answer.recoveryAttempt)
            .reduce(
              (total, answer) => total + Number(answer.recoveredPoints ?? 0),
              0,
            );

          return {
            initials: data.initials ?? "",
            playerDocId: data.playerDocId ?? "",
            isAdult: data.isAdult ?? "",
            ageGroup: data.ageGroup ?? "",
            cedula: data.cedula ?? "",
            participantId: data.participantId ?? "",
            participantKey: data.participantKey ?? "",
            id: docSnap.id,
            blueCount: data.blueCount ?? "",
            greenCount: data.greenCount ?? "",
            starCount: data.starCount ?? "",
            blueScore: data.blueScore ?? "",
            greenScore: data.greenScore ?? "",
            starScore: data.starScore ?? "",
            totalScore: Number(data.finalScore ?? 0) + recoveryScore,

            tier1question: tier1?.question ?? "",
            tier1answer: tier1?.selectedAnswer ?? "",
            tier1correct: tier1?.isCorrect ?? "",

            tier2question: tier2?.question ?? "",
            tier2answer: tier2?.selectedAnswer ?? "",
            tier2correct: tier2?.isCorrect ?? "",

            tier3question: tier3?.question ?? "",
            tier3answer: tier3?.selectedAnswer ?? "",
            tier3correct: tier3?.isCorrect ?? "",

            tier1answerRecov: tier1Recovery?.selectedAnswer ?? "",
            tier1correctRecov: tier1Recovery?.wasCorrect ?? "",

            tier2answerRecov: tier2Recovery?.selectedAnswer ?? "",
            tier2correctRecov: tier2Recovery?.wasCorrect ?? "",

            tier3answerRecov: tier3Recovery?.selectedAnswer ?? "",
            tier3correctRecov: tier3Recovery?.wasCorrect ?? "",
          };
        });

        const workbook = XLSX.utils.book_new();

        const playersSheet = XLSX.utils.json_to_sheet(playersRows);
        const sessionsSheet = XLSX.utils.json_to_sheet(sessionRows);

        XLSX.utils.book_append_sheet(workbook, playersSheet, "Participantes");
        XLSX.utils.book_append_sheet(workbook, sessionsSheet, "Sesiones");

        XLSX.writeFile(workbook, "verdeorigen-data.xlsx");
      } catch (error) {
        console.error("Error downloading BioRevolution data:", error);

        const message =
          error?.code === "auth/popup-closed-by-user"
            ? language === "es"
              ? "La ventana de inicio de sesi\u00F3n se cerr\u00F3 antes de terminar."
              : "The sign-in window was closed before finishing."
            : error?.code === "auth/popup-blocked"
              ? language === "es"
                ? "El navegador bloque\u00F3 la ventana de inicio de sesi\u00F3n."
                : "The browser blocked the sign-in window."
              : error?.code === "auth/unauthorized-domain"
                ? language === "es"
                  ? "Este dominio no est\u00E1 autorizado en Firebase Authentication."
                  : "This domain is not authorized in Firebase Authentication."
                : language === "es"
                  ? `No se pudo descargar el archivo. ${error?.code ?? ""}`
                  : `The file could not be downloaded. ${error?.code ?? ""}`;

        alert(message);
      }
    };

    window.addEventListener("biorev-download-excel", downloadExcel);

    return () => {
      window.removeEventListener("biorev-download-excel", downloadExcel);
    };
  }, [language]);

  useEffect(() => {
    const recordAnswer = async (event) => {
      const gameSessionId = window.currentBiorevPlayer?.gameSessionId;
      if (!gameSessionId) return;

      await updateDoc(doc(db, "biorevGameSessions", gameSessionId), {
        answers: arrayUnion(event.detail),
        updatedAt: serverTimestamp(),
      });
    };

    const finishGameSession = async (event) => {
      const gameSessionId = window.currentBiorevPlayer?.gameSessionId;
      if (!gameSessionId) return;

      await updateDoc(doc(db, "biorevGameSessions", gameSessionId), {
        finalScore: event.detail.finalScore,
        blueScore: event.detail.blueScore,
        greenScore: event.detail.greenScore,
        starScore: event.detail.starScore,
        blueCount: event.detail.blueCount,
        greenCount: event.detail.greenCount,
        starCount: event.detail.starCount,
        status: "finished",
        finishedAt: serverTimestamp(),
      });
    };

    const recordRecoveryAnswer = async (event) => {
      const gameSessionId = window.currentBiorevPlayer?.gameSessionId;
      if (!gameSessionId) return;

      await updateDoc(doc(db, "biorevGameSessions", gameSessionId), {
        answers: arrayUnion({
          ...event.detail,
          recoveryAttempt: true,
        }),
        updatedAt: serverTimestamp(),
      });
    };

    window.addEventListener("biorev-record-answer", recordAnswer);
    window.addEventListener(
      "biorev-record-recovery-answer",
      recordRecoveryAnswer,
    );
    window.addEventListener("biorev-finish-game-session", finishGameSession);

    return () => {
      window.removeEventListener("biorev-record-answer", recordAnswer);

      window.removeEventListener(
        "biorev-record-recovery-answer",
        recordRecoveryAnswer,
      );

      window.removeEventListener(
        "biorev-finish-game-session",
        finishGameSession,
      );
    };
  }, []);

  const isSpanish = language === "es";

  const text = {
    controls: isSpanish ? "Controles" : "Controls",
    jump: isSpanish ? "Saltar" : "Jump",
    diveRun: isSpanish ? "Agacharse / Correr" : "Dive / Run",
    time: isSpanish ? "Tiempo" : "Time",
    total: "Total",
    powers: isSpanish ? "Poderes" : "Powers",
    vacuum: isSpanish ? "Gravedad" : "Vacuum",
    storm: isSpanish ? "Tormenta" : "Storm",
    bounce: isSpanish ? "Goma" : "Bounce",
    ageQuestion: isSpanish
      ? "\u00BFTienes 18 a\u00F1os o m\u00E1s?"
      : "Are you 18 years old or older?",
    chooseOption: isSpanish
      ? "Selecciona una opci\u00F3n para continuar."
      : "Choose an option to continue.",
    adultYes: isSpanish
      ? "S\u00CD, TENGO 18 O M\u00C1S"
      : "YES, I AM 18 OR OLDER",
    adultNo: isSpanish ? "NO, SOY MENOR DE EDAD" : "NO, I AM UNDER 18",
    adultInfo: isSpanish ? "Informaci\u00F3n del adulto" : "Adult information",
    fullName: isSpanish ? "Nombre completo" : "Full name",
    nameCheckHelp: isSpanish
      ? "Primero escribe tu nombre completo. Comprobaremos si ya existe un perfil con ese nombre."
      : "First enter your full name. We will check whether a profile with that name already exists.",
    checkName: isSpanish ? "COMPROBAR NOMBRE" : "CHECK NAME",
    checkingName: isSpanish ? "Buscando nombre..." : "Checking name...",
    nameNotFound: isSpanish
      ? "No encontramos ese nombre. Completa los dem\u00E1s datos para crear tu perfil."
      : "We did not find that name. Complete the remaining information to create your profile.",
    sameNameFound: isSpanish
      ? "Encontramos perfiles con ese nombre. Revisa los datos y selecciona el tuyo."
      : "We found profiles with that name. Review the details and select yours.",
    documentEnding: isSpanish ? "Documento" : "Document",
    yesThisIsMe: isSpanish ? "S\u00CD, SOY YO" : "YES, THIS IS ME",
    noneIsMe: isSpanish ? "NINGUNO SOY YO" : "NONE OF THESE IS ME",
    differentPersonHelp: isSpanish
      ? "Completa tus propios datos. Se crear\u00E1 un perfil distinto aunque el nombre sea el mismo."
      : "Complete your own details. A separate profile will be used even though the name is the same.",
    selectedProfile: isSpanish ? "Perfil seleccionado" : "Profile selected",
    changeProfile: isSpanish ? "ELEGIR OTRO PERFIL" : "CHOOSE ANOTHER PROFILE",
    nameCheckError: isSpanish
      ? "No pudimos comprobar el nombre. Int\u00E9ntalo de nuevo."
      : "We could not check the name. Please try again.",
    identityDocument: isSpanish
      ? "C\u00E9dula u otro documento de identidad"
      : "National ID, passport, or other identity document",
    email: isSpanish ? "Correo electr\u00F3nico" : "Email",
    start: isSpanish ? "Jugar" : "Play",
    back: isSpanish ? "Atr\u00E1s" : "Back",
    minorIdQuestion: isSpanish
      ? "Elige tu ID de jugador"
      : "Choose your player ID",
    minorChoiceHelp: isSpanish
      ? "Si tienes autorizaci\u00F3n para usar tu correo institucional, puedes usarlo como tu ID. Tambi\u00E9n puedes generar un ID, o utilizar uno que hayas previamente generado."
      : "If you are authorized to use your institutional email, you can use it as your ID. You can also enter an ID you already have or generate one if you have not already.",
    minorPrivacy: isSpanish
      ? "Si no usas un correo institucional, no te pediremos tu nombre, documento ni correo electr\u00F3nico."
      : "If you do not use an institutional email, we will not ask for your name, identity document, or email.",
    useInstitutionEmail: isSpanish
      ? "USAR MI CORREO INSTITUCIONAL"
      : "USE MY INSTITUTIONAL EMAIL",
    institutionEmailTitle: isSpanish
      ? "Usa tu correo institucional como ID"
      : "Use your institutional email as your ID",
    institutionEmailHelp: isSpanish
      ? "Escribe \u00FAnicamente un correo institucional que tengas autorizaci\u00F3n para usar. Este correo ser\u00E1 tu ID de jugador."
      : "Enter only an institutional email that you are authorized to use. This email will become your player ID.",
    institutionEmail: isSpanish
      ? "Correo institucional"
      : "Institutional email",
    emailAlreadyUsed: isSpanish
      ? 'Ese correo ya est\u00E1 siendo usado como ID. Si es tuyo, regresa y elige "Ya tengo un ID o correo institucional" para continuar con ese perfil.'
      : 'That email is already being used as an ID. If it is yours, go back and choose "I already have an ID or institutional email" to continue with that profile.',
    invalidEmail: isSpanish
      ? "Ingresa un correo electr\u00F3nico v\u00E1lido."
      : "Enter a valid email address.",
    yesHaveId: isSpanish
      ? "S\u00CD, YA TENGO UN ID O CORREO INSTITUCIONAL"
      : "YES, I HAVE AN ID OR INSTITUTIONAL EMAIL",
    noGenerateId: isSpanish ? "GENERAR UN ID" : "GENERATE AN ID",
    enterId: isSpanish ? "Ingresa tu ID de jugador" : "Enter your player ID",
    existingIdHelp: isSpanish
      ? "Ingresa el c\u00F3digo de seis caracteres que recibiste o el correo institucional que ya registraste como ID."
      : "Enter the six-character code you received or the institutional email you previously registered as your ID.",
    playerId: isSpanish
      ? "ID de jugador o correo institucional"
      : "Player ID or institutional email",
    checkingId: isSpanish ? "Verificando ID..." : "Checking ID...",
    idNotFound: isSpanish
      ? "No encontramos ese ID. Rev\u00EDsalo o crea uno nuevo."
      : "We could not find that ID. Check it or create a new one.",
    idError: isSpanish
      ? "No se pudo verificar el ID. Int\u00E9ntalo de nuevo."
      : "The ID could not be checked. Please try again.",
    generatedIdTitle: isSpanish ? "Tu ID de jugador" : "Your player ID",
    generatedIdMessage: isSpanish
      ? "An\u00F3talo y gu\u00E1rdalo. Lo necesitar\u00E1s para volver a jugar con el mismo perfil."
      : "Write it down and keep it. You will need it to play again with the same profile.",
    codeChanged: isSpanish
      ? "Ese c\u00F3digo acaba de ser usado. Generamos uno nuevo; anota el c\u00F3digo actualizado."
      : "That code was just claimed. We generated a new one; write down the updated code.",
    saving: isSpanish ? "Guardando..." : "Saving...",
    genericSaveError: isSpanish
      ? "No se pudo iniciar la partida. Int\u00E9ntalo de nuevo."
      : "The game could not be started. Please try again.",
  };

  const normalize = (value) => value.trim().toLowerCase();
  const normalizeParticipantId = (value) => {
    const trimmedValue = value.trim();

    return trimmedValue.includes("@")
      ? trimmedValue.toLowerCase()
      : trimmedValue.toUpperCase().replace(/[^A-Z0-9]/g, "");
  };
  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const getInitials = (fullName) => {
    return fullName
      .trim()
      .split(/\s+/)
      .map((part) => part[0]?.toUpperCase())
      .join("");
  };

  const handlePlayerChange = (field, value) => {
    if (field === "fullName") {
      setPlayerDocId(null);
      setAdultNameStatus("idle");
      setAdultNameMatches([]);
      setPlayerForm((current) => ({
        ...current,
        fullName: value,
        cedula: "",
        email: "",
      }));
      return;
    }

    if (field === "cedula" || field === "participantId") {
      setPlayerDocId(null);
      setMinorIdStatus("idle");
    }

    setPlayerForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const findPlayerByParticipantId = async (rawId, isAdult) => {
    const normalizedId = normalizeParticipantId(rawId);
    const searchValues = [...new Set([rawId.trim(), normalizedId])];

    for (const searchValue of searchValues) {
      if (!searchValue) continue;

      const q = query(
        collection(db, "biorevPlayers"),
        where("participantIdSearch", "==", searchValue),
      );
      const snapshot = await getDocs(q);
      const match = snapshot.docs.find(
        (candidate) => candidate.data().isAdult === isAdult,
      );

      if (match) return match;
    }

    return null;
  };

  const maskDocument = (value = "") => {
    const cleanValue = String(value).trim();
    if (!cleanValue) return "----";
    const visible = cleanValue.slice(-4);
    return `${"\u2022".repeat(Math.max(2, cleanValue.length - visible.length))}${visible}`;
  };

  const maskEmail = (value = "") => {
    const [localPart = "", domain = ""] = String(value).trim().split("@");
    if (!localPart || !domain) return "\u2022\u2022\u2022\u2022";

    const [domainName = "", ...domainSuffix] = domain.split(".");
    const maskedLocal = `${localPart[0]}${"\u2022".repeat(Math.max(2, localPart.length - 1))}`;
    const maskedDomain = `${domainName[0] || "\u2022"}${"\u2022".repeat(Math.max(2, domainName.length - 1))}`;
    const suffix = domainSuffix.length ? `.${domainSuffix.join(".")}` : "";

    return `${maskedLocal}@${maskedDomain}${suffix}`;
  };

  const checkAdultName = async () => {
    const fullName = playerForm.fullName.trim();
    if (!fullName || adultNameStatus === "checking") return;

    if (document.activeElement?.matches?.("input, textarea")) {
      document.activeElement.blur();
    }

    setAdultNameStatus("checking");
    setAdultNameMatches([]);
    setPlayerDocId(null);
    setPlayerForm((current) => ({ ...current, cedula: "", email: "" }));

    try {
      const nameQuery = query(
        collection(db, "biorevPlayers"),
        where("fullNameSearch", "==", normalize(fullName)),
      );
      const snapshot = await getDocs(nameQuery);
      const matches = snapshot.docs
        .filter((candidate) => {
          const data = candidate.data();
          return data.isAdult === true || data.ageGroup === "adult";
        })
        .map((candidate) => ({ id: candidate.id, ...candidate.data() }))
        .sort((a, b) =>
          String(a.participantId ?? a.cedula ?? "").localeCompare(
            String(b.participantId ?? b.cedula ?? ""),
          ),
        );

      setAdultNameMatches(matches);
      setAdultNameStatus(matches.length > 0 ? "matches" : "manual");
    } catch (error) {
      console.error("Error finding adult participants by name:", error);
      setAdultNameStatus("error");
    }
  };

  const selectAdultMatch = (match) => {
    setPlayerDocId(match.id);
    setPlayerForm({
      fullName: match.fullName ?? playerForm.fullName.trim(),
      isAdult: true,
      cedula: match.cedula ?? match.participantId ?? "",
      email: match.email ?? "",
      participantId: "",
    });
    setAdultNameStatus("selected");
  };

  const useManualAdultEntry = () => {
    setPlayerDocId(null);
    setPlayerForm((current) => ({
      ...current,
      isAdult: true,
      cedula: "",
      email: "",
      participantId: "",
    }));
    setAdultNameStatus("manual");
  };

  const restartAdultNameCheck = () => {
    setPlayerDocId(null);
    setAdultNameStatus("idle");
    setAdultNameMatches([]);
    setPlayerForm((current) => ({
      ...current,
      cedula: "",
      email: "",
    }));
  };

  const makeMinorIdCandidate = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const values = new Uint32Array(6);
    window.crypto.getRandomValues(values);

    return Array.from(
      values,
      (value) => alphabet[value % alphabet.length],
    ).join("");
  };

  const generateUniqueMinorId = async () => {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const candidate = makeMinorIdCandidate();
      const existing = await findPlayerByParticipantId(candidate, false);
      if (!existing) return candidate;
    }

    throw new Error("Could not generate a unique minor participant ID.");
  };

  const createMinorId = async () => {
    if (playerSubmitting) return;

    setPlayerSubmitting(true);
    setMinorIdStatus("checking");

    try {
      const participantId = await generateUniqueMinorId();
      setPlayerForm({
        fullName: "",
        isAdult: false,
        cedula: "",
        email: "",
        participantId,
      });
      setPlayerDocId(null);
      setMinorIdStatus("generated");
      setPlayerModalStep("minor-generated");
    } catch (error) {
      console.error("Error generating minor participant ID:", error);
      setMinorIdStatus("error");
    } finally {
      setPlayerSubmitting(false);
    }
  };

  const chooseAgeGroup = (isAdult) => {
    setPlayerDocId(null);
    setMinorIdStatus("idle");
    setAdultNameStatus("idle");
    setAdultNameMatches([]);
    setPlayerForm({
      fullName: "",
      isAdult,
      cedula: "",
      email: "",
      participantId: "",
    });
    setPlayerModalStep(isAdult ? "adult" : "minor-choice");
  };

  const goBackInPlayerModal = () => {
    setPlayerDocId(null);
    setMinorIdStatus("idle");
    setAdultNameStatus("idle");
    setAdultNameMatches([]);

    if (playerModalStep === "adult" || playerModalStep === "minor-choice") {
      setPlayerModalStep("age");
      return;
    }

    setPlayerModalStep("minor-choice");
  };

  const submitPlayer = async (event) => {
    event.preventDefault();

    if (playerSubmitting) return;

    const isAdult = playerForm.isAdult === true;
    const fullName = isAdult ? playerForm.fullName.trim() : "";
    const cedula = isAdult ? playerForm.cedula.trim() : "";
    const minorUsesEmail =
      !isAdult &&
      isValidEmail(normalizeParticipantId(playerForm.participantId));
    const email = isAdult
      ? playerForm.email.trim()
      : minorUsesEmail
        ? normalizeParticipantId(playerForm.participantId)
        : "";
    const participantId = normalizeParticipantId(
      isAdult ? cedula : playerForm.participantId,
    );

    const formIsValid = isAdult
      ? Boolean(
          (adultNameStatus === "manual" || adultNameStatus === "selected") &&
          fullName &&
          cedula &&
          email,
        )
      : playerModalStep === "minor-email"
        ? isValidEmail(participantId)
        : playerModalStep === "minor-existing"
          ? participantId.length === 6 || isValidEmail(participantId)
          : participantId.length === 6;

    if (!formIsValid) return;

    setPlayerSubmitting(true);

    try {
      let activePlayerDocId = playerDocId;
      const existingPlayer = await findPlayerByParticipantId(
        participantId,
        isAdult,
      );

      if (!isAdult && playerModalStep === "minor-email" && existingPlayer) {
        setMinorIdStatus("email-used");
        return;
      }

      if (!isAdult && playerModalStep === "minor-existing" && !existingPlayer) {
        setMinorIdStatus("not-found");
        return;
      }

      if (!isAdult && playerModalStep === "minor-generated" && existingPlayer) {
        const replacementId = await generateUniqueMinorId();
        setPlayerForm((current) => ({
          ...current,
          participantId: replacementId,
        }));
        setMinorIdStatus("regenerated");
        return;
      }

      if (existingPlayer) {
        activePlayerDocId = existingPlayer.id;
      }

      const participantKey = `${isAdult ? "adult" : "minor"}:${participantId}`;
      const payload = {
        fullName,
        isAdult,
        ageGroup: isAdult ? "adult" : "minor",
        cedula,
        participantId,
        participantKey,
        email,
        fullNameSearch: isAdult ? normalize(fullName) : "",
        cedulaSearch: isAdult ? participantId : "",
        participantIdSearch: participantId,
        emailSearch: email ? normalize(email) : "",
        updatedAt: serverTimestamp(),
      };

      if (activePlayerDocId) {
        await updateDoc(doc(db, "biorevPlayers", activePlayerDocId), payload);
      } else if (!isAdult) {
        const minorPlayerRef = doc(
          db,
          "biorevPlayers",
          `minor-${encodeURIComponent(participantId)}`,
        );

        try {
          await runTransaction(db, async (transaction) => {
            const existingMinor = await transaction.get(minorPlayerRef);

            if (existingMinor.exists()) {
              const collisionError = new Error("Minor ID collision");
              collisionError.code = "biorev/minor-id-collision";
              throw collisionError;
            }

            transaction.set(minorPlayerRef, {
              ...payload,
              createdAt: serverTimestamp(),
            });
          });

          activePlayerDocId = minorPlayerRef.id;
          setPlayerDocId(minorPlayerRef.id);
        } catch (error) {
          if (error?.code !== "biorev/minor-id-collision") throw error;

          if (playerModalStep === "minor-email") {
            setMinorIdStatus("email-used");
            return;
          }

          const replacementId = await generateUniqueMinorId();
          setPlayerForm((current) => ({
            ...current,
            participantId: replacementId,
          }));
          setMinorIdStatus("regenerated");
          return;
        }
      } else {
        const created = await addDoc(collection(db, "biorevPlayers"), {
          ...payload,
          createdAt: serverTimestamp(),
        });

        activePlayerDocId = created.id;
        setPlayerDocId(created.id);
      }

      const gameSession = await addDoc(collection(db, "biorevGameSessions"), {
        playerDocId: activePlayerDocId,
        isAdult,
        ageGroup: isAdult ? "adult" : "minor",
        cedula,
        participantId,
        participantKey,
        initials: isAdult ? getInitials(fullName) : "",
        answers: [],
        finalScore: null,
        status: "started",
        startedAt: serverTimestamp(),
        finishedAt: null,
      });

      window.currentBiorevPlayer = {
        playerDocId: activePlayerDocId,
        gameSessionId: gameSession.id,
        isAdult,
        cedula,
        participantId,
        participantKey,
      };

      window.dispatchEvent(new Event("biorev-game-loading-start"));

      window.dispatchEvent(
        new CustomEvent("agritech-player-ready", {
          detail: {
            playerDocId: activePlayerDocId,
            fullName,
            isAdult,
            cedula,
            participantId,
            participantKey,
            email,
          },
        }),
      );

      setPlayerModalOpen(false);
    } catch (error) {
      console.error("Error starting player session:", error);
      alert(text.genericSaveError);
    } finally {
      setPlayerSubmitting(false);
    }
  };

  return (
    <main
      id="biorevolution-app"
      className="relative min-h-screen w-screen bg-[#06130c] flex justify-center items-center"
      onPointerUpCapture={(event) => {
        if (event.pointerType !== "touch") return;

        const touchLandscape = window.matchMedia(
          "(orientation: landscape)",
        ).matches;

        if (!touchLandscape) return;

        if (event.target.closest("#player-info-modal")) return;
        if (event.target.closest("input, textarea, select")) return;

        const fullscreenSupported =
          document.fullscreenEnabled === true ||
          document.webkitFullscreenEnabled === true;

        if (!fullscreenSupported) return;

        if (document.fullscreenElement || document.webkitFullscreenElement) {
          return;
        }

        const fullscreenTarget = event.currentTarget;

        const requestFullscreen =
          fullscreenTarget.requestFullscreen ||
          fullscreenTarget.webkitRequestFullscreen;

        if (!requestFullscreen) return;

        try {
          const result = requestFullscreen.call(fullscreenTarget);

          if (result?.catch) {
            result.catch(() => {});
          }
        } catch {
          // Some mobile browsers do not expose element fullscreen.
        }
      }}
    >
      <MobileGameControls isSpanish={isSpanish} />

      {playerModalOpen && (
        <div
          id="player-info-modal"
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/70"
          onMouseDown={() => {
            setPlayerModalOpen(false);
            window.dispatchEvent(new Event("agritech-player-modal-closed"));
          }}
        >
          <form
            id="player-info-form"
            data-player-step={playerModalStep}
            data-adult-status={
              playerModalStep === "adult" ? adultNameStatus : undefined
            }
            onMouseDown={(e) => e.stopPropagation()}
            onSubmit={submitPlayer}
            className="w-[400px] max-h-[calc(100%-32px)] overflow-y-auto rounded-xl border-2 border-[#769b55] bg-[#18231D] p-5 text-[#f7faf4] shadow-[0_12px_30px_rgba(0,0,0,0.35)]"
          >
            {playerModalStep !== "age" && (
              <button
                type="button"
                onClick={goBackInPlayerModal}
                className="mb-3 text-sm font-bold text-[#cbd5c0] hover:text-[#d9f99d]"
              >
                {"\u2190"} {text.back}
              </button>
            )}

            <h2
              className="text-2xl text-[#ecfccb]"
              style={{ fontFamily: '"Russo One", sans-serif' }}
            >
              {playerModalStep === "age"
                ? text.ageQuestion
                : playerModalStep === "adult"
                  ? text.adultInfo
                  : playerModalStep === "minor-choice"
                    ? text.minorIdQuestion
                    : playerModalStep === "minor-existing"
                      ? text.enterId
                      : playerModalStep === "minor-email"
                        ? text.institutionEmailTitle
                        : text.generatedIdTitle}
            </h2>

            {playerModalStep === "age" && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-[#d9e4d5]">{text.chooseOption}</p>

                <button
                  type="button"
                  onClick={() => chooseAgeGroup(true)}
                  className="w-full rounded-lg border-2 border-[#d9f99d] bg-[#65a30d] px-4 py-3 text-sm font-black text-white hover:bg-[#4d7c0f]"
                >
                  {text.adultYes}
                </button>

                <button
                  type="button"
                  onClick={() => chooseAgeGroup(false)}
                  className="w-full rounded-lg border-2 border-[#769b55] bg-[#0b1510] px-4 py-3 text-sm font-black text-[#ecfccb] hover:bg-[#26382b]"
                >
                  {text.adultNo}
                </button>
              </div>
            )}

            {playerModalStep === "adult" && (
              <>
                <div id="adult-player-content" className="mt-4 space-y-3">
                  {adultNameStatus === "selected" ? (
                    <div className="rounded-lg border border-[#769b55] bg-[#0b1510]/70 px-3 py-3">
                      <p className="text-sm font-bold text-[#d9e4d5]">
                        {text.selectedProfile}
                      </p>
                      <p className="mt-2 text-xs font-bold text-[#aebdaa]">
                        {text.fullName}
                      </p>
                      <p className="mt-1 font-bold text-[#ecfccb]">
                        {playerForm.fullName}
                      </p>
                    </div>
                  ) : (
                    <label className="block">
                      <span className="text-sm font-bold text-[#d9e4d5]">
                        {text.fullName}
                      </span>
                      <input
                        value={playerForm.fullName}
                        autoComplete="name"
                        enterKeyHint="done"
                        onChange={(e) =>
                          handlePlayerChange("fullName", e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            checkAdultName();
                          }
                        }}
                        className="mt-1.5 w-full rounded-lg border border-[#405747] bg-[#0b1510] px-3 py-2 text-[#f7faf4] outline-none focus:border-[#769b55]"
                      />
                    </label>
                  )}

                  {(adultNameStatus === "idle" ||
                    adultNameStatus === "error") && (
                    <>
                      <p className="text-sm leading-5 text-[#cbd5c0]">
                        {text.nameCheckHelp}
                      </p>

                      {adultNameStatus === "error" && (
                        <p className="rounded-lg border border-red-500/50 bg-red-950/40 px-3 py-2 text-sm font-semibold text-red-200">
                          {text.nameCheckError}
                        </p>
                      )}

                      <button
                        type="button"
                        disabled={!playerForm.fullName.trim()}
                        onClick={checkAdultName}
                        className={`w-full rounded-lg border-2 px-4 py-2.5 text-sm font-black ${
                          !playerForm.fullName.trim()
                            ? "cursor-not-allowed border-[#405747] bg-[#31402f] text-[#849184]"
                            : "border-[#d9f99d] bg-[#65a30d] text-white hover:bg-[#4d7c0f]"
                        }`}
                      >
                        {text.checkName}
                      </button>
                    </>
                  )}

                  {adultNameStatus === "checking" && (
                    <p className="rounded-lg border border-yellow-500/50 bg-yellow-950/40 px-3 py-2 text-sm font-semibold text-yellow-200">
                      {text.checkingName}
                    </p>
                  )}

                  {adultNameStatus === "matches" && (
                    <div className="space-y-3">
                      <p className="rounded-lg border border-[#769b55] bg-[#0b1510]/70 px-3 py-2 text-sm leading-5 text-[#d9e4d5]">
                        {text.sameNameFound}
                      </p>

                      <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
                        {adultNameMatches.map((match) => (
                          <div
                            key={match.id}
                            className="rounded-lg border border-[#405747] bg-[#0b1510] p-3"
                          >
                            <p className="font-bold text-[#ecfccb]">
                              {match.fullName || playerForm.fullName}
                            </p>
                            <p className="mt-1 text-xs leading-5 text-[#cbd5c0]">
                              {text.documentEnding}:{" "}
                              {maskDocument(
                                match.cedula ?? match.participantId,
                              )}
                              <br />
                              {text.email}: {maskEmail(match.email)}
                            </p>
                            <button
                              type="button"
                              onClick={() => selectAdultMatch(match)}
                              className="mt-2 w-full rounded-md border border-[#d9f99d] bg-[#4d7c0f] px-3 py-2 text-xs font-black text-white hover:bg-[#65a30d]"
                            >
                              {text.yesThisIsMe}
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={useManualAdultEntry}
                        className="w-full rounded-lg border-2 border-[#769b55] bg-[#0b1510] px-4 py-2.5 text-sm font-black text-[#ecfccb] hover:bg-[#26382b]"
                      >
                        {text.noneIsMe}
                      </button>
                    </div>
                  )}

                  {adultNameStatus === "manual" && (
                    <>
                      <p className="rounded-lg border border-[#769b55] bg-[#0b1510]/70 px-3 py-2 text-sm leading-5 text-[#d9e4d5]">
                        {adultNameMatches.length > 0
                          ? text.differentPersonHelp
                          : text.nameNotFound}
                      </p>

                      <label className="block">
                        <span className="text-sm font-bold text-[#d9e4d5]">
                          {text.identityDocument}
                        </span>
                        <input
                          value={playerForm.cedula}
                          onChange={(e) =>
                            handlePlayerChange("cedula", e.target.value)
                          }
                          className="mt-1.5 w-full rounded-lg border border-[#405747] bg-[#0b1510] px-3 py-2 text-[#f7faf4] outline-none focus:border-[#769b55]"
                        />
                      </label>

                      <label className="block">
                        <span className="text-sm font-bold text-[#d9e4d5]">
                          {text.email}
                        </span>
                        <input
                          type="email"
                          value={playerForm.email}
                          onChange={(e) =>
                            handlePlayerChange("email", e.target.value)
                          }
                          className="mt-1.5 w-full rounded-lg border border-[#405747] bg-[#0b1510] px-3 py-2 text-[#f7faf4] outline-none focus:border-[#769b55]"
                        />
                      </label>
                    </>
                  )}

                  {adultNameStatus === "selected" && (
                    <button
                      type="button"
                      onClick={restartAdultNameCheck}
                      className="w-full rounded-lg border border-[#769b55] bg-[#0b1510] px-3 py-2 text-xs font-black text-[#ecfccb] hover:bg-[#26382b]"
                    >
                      {text.changeProfile}
                    </button>
                  )}
                </div>

                {(adultNameStatus === "manual" ||
                  adultNameStatus === "selected") && (
                  <button
                    type="submit"
                    disabled={
                      playerSubmitting ||
                      !playerForm.fullName.trim() ||
                      !playerForm.cedula.trim() ||
                      !playerForm.email.trim()
                    }
                    className={`mt-4 w-full rounded-lg border-2 px-5 py-2.5 text-base font-black ${
                      playerSubmitting ||
                      !playerForm.fullName.trim() ||
                      !playerForm.cedula.trim() ||
                      !playerForm.email.trim()
                        ? "cursor-not-allowed border-[#405747] bg-[#31402f] text-[#849184]"
                        : "border-[#d9f99d] bg-[#65a30d] text-white hover:bg-[#4d7c0f]"
                    }`}
                  >
                    {playerSubmitting ? text.saving : text.start}
                  </button>
                )}
              </>
            )}

            {playerModalStep === "minor-choice" && (
              <div className="mt-4 space-y-3">
                <p className="rounded-lg border border-[#405747] bg-[#0b1510]/70 px-3 py-2 text-sm leading-5 text-[#d9e4d5]">
                  {text.minorChoiceHelp}
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setPlayerDocId(null);
                    setMinorIdStatus("idle");
                    setPlayerForm((current) => ({
                      ...current,
                      isAdult: false,
                      email: "",
                      participantId: "",
                    }));
                    setPlayerModalStep("minor-email");
                  }}
                  className="w-full rounded-lg border-2 border-[#d9f99d] bg-[#65a30d] px-4 py-3 text-sm font-black text-white hover:bg-[#4d7c0f]"
                >
                  {text.useInstitutionEmail}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setPlayerDocId(null);
                    setMinorIdStatus("idle");

                    setPlayerForm((current) => ({
                      ...current,
                      isAdult: false,
                      participantId: "",
                    }));

                    setPlayerModalStep("minor-existing");
                  }}
                  className="w-full rounded-lg border-2 border-[#769b55] bg-[#0b1510] px-4 py-3 text-sm font-black text-[#ecfccb] hover:bg-[#26382b]"
                >
                  {text.yesHaveId}
                </button>

                <button
                  type="button"
                  disabled={playerSubmitting}
                  onClick={createMinorId}
                  className="w-full rounded-lg border-2 border-[#769b55] bg-[#0b1510] px-4 py-3 text-sm font-black text-[#ecfccb] hover:bg-[#26382b] disabled:cursor-wait disabled:opacity-60"
                >
                  {playerSubmitting ? text.checkingId : text.noGenerateId}
                </button>

                <p className="text-xs leading-5 text-[#cbd5c0]">
                  {text.minorPrivacy}
                </p>
              </div>
            )}

            {playerModalStep === "minor-email" && (
              <>
                <p className="mt-3 text-sm leading-5 text-[#d9e4d5]">
                  {text.institutionEmailHelp}
                </p>

                <label className="mt-4 block">
                  <span className="text-sm font-bold text-[#d9e4d5]">
                    {text.institutionEmail}
                  </span>
                  <input
                    type="email"
                    value={playerForm.participantId}
                    autoCapitalize="none"
                    autoComplete="email"
                    onChange={(e) =>
                      handlePlayerChange("participantId", e.target.value)
                    }
                    className="mt-1.5 w-full rounded-lg border border-[#405747] bg-[#0b1510] px-3 py-2 text-[#ecfccb] outline-none focus:border-[#769b55]"
                  />
                </label>

                {playerForm.participantId.trim() &&
                  !isValidEmail(playerForm.participantId) && (
                    <p className="mt-3 text-sm font-semibold text-red-300">
                      {text.invalidEmail}
                    </p>
                  )}

                {minorIdStatus === "email-used" && (
                  <p className="mt-3 rounded-lg border border-red-500/50 bg-red-950/40 px-3 py-2 text-sm font-semibold leading-5 text-red-200">
                    {text.emailAlreadyUsed}
                  </p>
                )}

                {minorIdStatus === "error" && (
                  <p className="mt-3 rounded-lg border border-red-500/50 bg-red-950/40 px-3 py-2 text-sm font-semibold leading-5 text-red-200">
                    {text.idError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={
                    playerSubmitting || !isValidEmail(playerForm.participantId)
                  }
                  className={`mt-4 w-full rounded-lg border-2 px-5 py-2.5 text-base font-black ${
                    playerSubmitting || !isValidEmail(playerForm.participantId)
                      ? "cursor-not-allowed border-[#405747] bg-[#31402f] text-[#849184]"
                      : "border-[#d9f99d] bg-[#65a30d] text-white hover:bg-[#4d7c0f]"
                  }`}
                >
                  {playerSubmitting ? text.checkingId : text.start}
                </button>
              </>
            )}

            {playerModalStep === "minor-existing" && (
              <>
                <p className="mt-3 text-sm leading-5 text-[#d9e4d5]">
                  {text.existingIdHelp}
                </p>

                <label className="mt-4 block">
                  <span className="text-sm font-bold text-[#d9e4d5]">
                    {text.playerId}
                  </span>
                  <input
                    value={playerForm.participantId}
                    autoCapitalize="none"
                    onChange={(e) =>
                      handlePlayerChange("participantId", e.target.value)
                    }
                    className="mt-1.5 w-full rounded-lg border border-[#405747] bg-[#0b1510] px-3 py-2 text-[#ecfccb] outline-none focus:border-[#769b55]"
                  />
                </label>

                {minorIdStatus !== "idle" && (
                  <p
                    className={`mt-3 text-sm font-semibold ${
                      minorIdStatus === "checking"
                        ? "text-yellow-300"
                        : minorIdStatus === "not-found"
                          ? "text-red-300"
                          : "text-red-300"
                    }`}
                  >
                    {minorIdStatus === "checking"
                      ? text.checkingId
                      : minorIdStatus === "not-found"
                        ? text.idNotFound
                        : text.idError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={
                    playerSubmitting ||
                    !(
                      normalizeParticipantId(playerForm.participantId)
                        .length === 6 || isValidEmail(playerForm.participantId)
                    )
                  }
                  className={`mt-4 w-full rounded-lg border-2 px-5 py-2.5 text-base font-black ${
                    playerSubmitting ||
                    !(
                      normalizeParticipantId(playerForm.participantId)
                        .length === 6 || isValidEmail(playerForm.participantId)
                    )
                      ? "cursor-not-allowed border-[#405747] bg-[#31402f] text-[#849184]"
                      : "border-[#d9f99d] bg-[#65a30d] text-white hover:bg-[#4d7c0f]"
                  }`}
                >
                  {playerSubmitting ? text.checkingId : text.start}
                </button>
              </>
            )}

            {playerModalStep === "minor-generated" && (
              <>
                <div className="mt-4 rounded-xl border-2 border-[#d9f99d] bg-[#0b1510] px-4 py-5 text-center">
                  <p className="font-mono text-4xl font-black tracking-[0.22em] text-[#d9f99d]">
                    {playerForm.participantId}
                  </p>
                </div>

                <p className="mt-4 text-sm font-semibold leading-6 text-[#f7faf4]">
                  {text.generatedIdMessage}
                </p>

                {minorIdStatus === "regenerated" && (
                  <p className="mt-3 rounded-lg border border-yellow-500/50 bg-yellow-950/40 px-3 py-2 text-xs font-semibold leading-5 text-yellow-200">
                    {text.codeChanged}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={playerSubmitting}
                  className="mt-4 w-full rounded-lg border-2 border-[#d9f99d] bg-[#65a30d] px-5 py-2.5 text-base font-black text-white hover:bg-[#4d7c0f] disabled:cursor-wait disabled:opacity-60"
                >
                  {playerSubmitting ? text.saving : text.start}
                </button>
              </>
            )}
          </form>
        </div>
      )}

      <div id="game-layout-shell" className="flex gap-6">
        <div
          id="left-ui-panel"
          className="w-[220px] h-[590px] bg-[#18231D] border border-[#405747] shadow-[0_12px_30px_rgba(0,0,0,0.35)] rounded-xl p-4 text-white flex flex-col"
        >
          <div className="developer-logo-content">
            <p className="developer-logo-label">Desarrollado por:</p>

            <img
              src="/assets/CES.png"
              alt="CES University"
              className="institution-logo"
            />

            <p className="developer-logo-label">En el marco de:</p>

            <img
              src="/assets/futumed.png"
              alt="Futumed"
              className="futumed-logo"
            />
          </div>
        </div>

        <div
          id="game-canvas-shell"
          className="
    relative
    bg-[#07130d]
    p-[6px]
    rounded-2xl
    border-2
    border-[#769b55]
    shadow-[0_0_18px_rgba(132,180,88,0.18),0_18px_45px_rgba(0,0,0,0.5)]
    scale-95
    origin-top
  "
        >
          {gameLoadingVisible && (
            <div
              className="game-loading-overlay"
              role="progressbar"
              aria-label={
                isSpanish ? "Progreso de carga" : "Loading progress"
              }
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(gameLoadingProgress * 100)}
            >
              <div className="game-loading-panel">
                <p className="game-loading-brand">
                  {isSpanish ? "VERDE ORIGEN" : "GREEN ORIGIN"}
                </p>
                <h2>
                  {isSpanish ? "Cargando el juego..." : "Loading game..."}
                </h2>
                <div className="game-loading-track">
                  <div
                    className="game-loading-fill"
                    style={{
                      width: `${Math.round(gameLoadingProgress * 100)}%`,
                    }}
                  />
                </div>
                <p className="game-loading-percentage">
                  {Math.round(gameLoadingProgress * 100)}%
                </p>
              </div>
            </div>
          )}
          <Game />
        </div>

        <div
          key={panelResetKey}
          id="right-ui-panel"
          className="w-[220px] h-[590px] bg-[#18231D] border border-[#405747] shadow-[0_12px_30px_rgba(0,0,0,0.35)] rounded-xl p-4 text-white"
        >
          <div className="h-full flex flex-col">
            <div className="right-panel-logo-area">
              <img
                src="/assets/Ruta-N.png"
                alt="Ruta N"
                className="ruta-n-logo"
              />
            </div>

            <div id="right-panel-gameplay-content" className="space-y-4">
              <div
                id="gameplay-timer"
                className="flex flex-col items-center justify-center pt-10 pb-6"
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-gray-400">
                  {text.time}
                </p>
                <p
                  id="ui-time"
                  className="mt-2 text-7xl font-black leading-none"
                >
                  70
                </p>
              </div>

              <div
                id="results-counters"
                className="space-y-4"
                style={{ display: "none" }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-end gap-2">
                    <div className="relative w-10 h-10">
                      <img
                        src="/assets/bluedrop.png"
                        className="w-10 h-10"
                        alt=""
                      />
                      <span className="absolute bottom-0 right-0 text-[10px] text-white font-bold">
                        x<span id="ui-blue-count">0</span>
                      </span>
                    </div>
                    <span className="text-gray-500">=</span>
                  </div>
                  <span id="ui-blue-score" className="text-lg font-semibold">
                    0
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-end gap-2">
                    <div className="relative w-10 h-10">
                      <img
                        src="/assets/greendrop.png"
                        className="w-10 h-10"
                        alt=""
                      />
                      <span className="absolute bottom-0 right-0 text-[10px] text-white font-bold">
                        x<span id="ui-green-count">0</span>
                      </span>
                    </div>
                    <span className="text-gray-500">=</span>
                  </div>
                  <span id="ui-green-score" className="text-lg font-semibold">
                    0
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-end gap-2">
                    <div className="relative w-10 h-10">
                      <img
                        src="/assets/star.png"
                        className="w-12 h-12 object-contain translate-y-1.5"
                        alt=""
                      />
                      <span className="absolute bottom-0 right-0 text-[10px] text-white font-bold">
                        x<span id="ui-star-count">0</span>
                      </span>
                    </div>
                    <span className="text-gray-500">=</span>
                  </div>
                  <span id="ui-star-score" className="text-lg font-semibold">
                    0
                  </span>
                </div>
              </div>

              <div
                id="results-total"
                className="pt-4 border-t border-gray-600 flex justify-between items-center"
                style={{ display: "none" }}
              >
                <p className="text-sm text-gray-400">{text.total}</p>
                <p id="ui-score" className="text-3xl font-bold">
                  0
                </p>
              </div>

              <div
                id="gameplay-powers"
                className="pt-4 border-t border-gray-600"
              >
                <p className="text-sm text-gray-400 mb-3">{text.powers}</p>

                <div className="flex flex-col gap-3">
                  <div
                    id="ui-vacuum-row"
                    className="flex items-center gap-3 text-gray-500"
                  >
                    <div
                      id="ui-vacuum-key"
                      className="relative w-10 h-10 rounded-lg border border-gray-600 bg-gray-800 flex items-center justify-center text-lg font-bold overflow-hidden"
                    >
                      <span>1</span>
                      <span
                        id="ui-vacuum-timer"
                        className="hidden absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold"
                      ></span>
                    </div>
                    <p
                      id="ui-vacuum-label"
                      className="text-sm font-semibold tracking-wide"
                    >
                      {text.vacuum}
                    </p>
                  </div>

                  <div
                    id="ui-storm-row"
                    className="flex items-center gap-3 text-gray-500"
                  >
                    <div
                      id="ui-storm-key"
                      className="relative w-10 h-10 rounded-lg border border-gray-600 bg-gray-800 flex items-center justify-center text-lg font-bold overflow-hidden"
                    >
                      <span>2</span>
                      <span
                        id="ui-storm-timer"
                        className="hidden absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold"
                      ></span>
                    </div>
                    <p
                      id="ui-storm-label"
                      className="text-sm font-semibold tracking-wide"
                    >
                      {text.storm}
                    </p>
                  </div>

                  <div
                    id="ui-bounce-row"
                    className="flex items-center gap-3 text-gray-500"
                  >
                    <div
                      id="ui-bounce-key"
                      className="relative w-10 h-10 rounded-lg border border-gray-600 bg-gray-800 flex items-center justify-center text-lg font-bold overflow-hidden"
                    >
                      <span>3</span>
                      <span
                        id="ui-bounce-timer"
                        className="hidden absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold"
                      ></span>
                    </div>
                    <p
                      id="ui-bounce-label"
                      className="text-sm font-semibold tracking-wide"
                    >
                      {text.bounce}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default App;
